import { ReactNode } from "react";
import { FieldPath, FieldValues, UseControllerProps, useController } from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { Input } from "../inputs/Input";

type FormInputBaseProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	compact?: boolean;
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	disabled?: boolean;
	rightAlign?: boolean;
	errorMessage?: string;
	maxLength?: number;
	uppercase?: boolean;
	prefix?: ReactNode;
	suffix?: ReactNode;
	testId?: string;
	onBlur?: () => void;
};

export type InputFilterProps = OnlyCharactersProps | OnlyNumbersProps | NeitherProps;

type OnlyCharactersProps = {
	onlyCharacters: true;
	onlyNumbers?: never;
};

type OnlyNumbersProps = {
	onlyCharacters?: never;
	onlyNumbers: true;
};

type NeitherProps = {
	onlyCharacters?: never;
	onlyNumbers?: never;
};

export type FormInputProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = FormInputBaseProps<TFieldValues, TTransformedValues> &
	(OnlyCharactersProps | OnlyNumbersProps | NeitherProps);

export const FormInput = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	name,
	control,
	defaultValue,
	compact,
	className,
	containerClassName,
	placeholder,
	disabled,
	rightAlign,
	errorMessage,
	maxLength,
	uppercase,
	onlyCharacters,
	onlyNumbers,
	prefix,
	suffix,
	testId,
	onBlur,
}: FormInputProps<TFieldValues, TTransformedValues>) => {
	const {
		field,
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const displayValue = field.value ?? "";
	const hasError = !!error;

	return (
		<div className="relative">
			<Input
				{...field}
				value={displayValue}
				onChange={(e) => {
					let value = e.target.value;

					if (onlyCharacters) {
						value = value.replace(/[^a-zA-Z]/g, "");
					} else if (onlyNumbers) {
						value = value.replace(/[^0-9]/g, "");
					}

					if (uppercase) {
						value = value.toUpperCase();
					}

					field.onChange(value === "" ? null : value);
				}}
				onBlur={() => {
					field.onBlur();
					onBlur?.();
				}}
				compact={compact}
				className={className}
				containerClassName={containerClassName}
				placeholder={placeholder}
				disabled={disabled}
				rightAlign={rightAlign}
				maxLength={maxLength}
				type={"text"}
				error={hasError}
				prefix={prefix}
				suffix={suffix}
				testId={testId}
			/>
			{hasError && <FormFieldError error={error} errorMessage={errorMessage} />}
		</div>
	);
};
